/* eslint-disable import/no-anonymous-default-export */
const d3 = require('d3')
const moment = require('moment');
const { parseDate } = require('../utils/formatters.js');
const { wrap } = require('../utils/shared-functions.js');
const { TRANSLATE_GENERAL_TAG } = require('../consts.js');

const COLORS = {
  DIASTOLIC_MIX_COLOR: '#9d9d9d',
  SYSTOLIC_MIX_COLOR: '#292a29',
  DIASTOLIC_COLOR: '#9AC03A',
  SYSTOLIC_COLOR: '#449B26',
  DIASTOLIC_MANUAL_COLOR: '#7fc7f4',
  SYSTOLIC_MANUAL_COLOR: '#558AC2',
  DIASTOLIC_EXTERNAL_COLOR: '#EC9DFF',
  SYSTOLIC_EXTERNAL_COLOR: '#CF42F0',
  DIASTOLIC_BACKGROUND_COLOR: '#831111',
  SYSTOLIC_BACKGROUND_COLOR: '#1B6186',
  EMPTY_COLOR: '#000000',
}

const BAR_WIDTH = 12;
const MAX_BP_SYS = 300;
const MIN_BP_SYS = 80;
const MAX_BP_DIA = 120;
const MIN_BP_DIA = 40;
const TRANSLATE_TAG = 'charts.blood_pressure'

exports.createBPChart = function (systolic, systolic_manual, systolic_external, diastolic, diastolic_manual, diastolic_external ,thresholds, onClick, width, height,appConfigThresholds, translate) {
  const { 'Blood pressure': blood_pressure_threshold } = thresholds;
  const margin = { top: 70, right: 30, bottom: 40, left: 50 };

  const data = systolic.map((d, i) => {
    const date = parseDate(moment(d['date']).format('YYYY-MM-DD'));
    const { high: syshigh_r, low: syslow_r, avg: sysavg } = systolic[i];
    const { low: diaslow_r, high: diashigh_r, avg: diasavg } = diastolic[i];
    const { high: syshigh_man, low: syslow_man, avg: sysavg_man } = systolic_manual[i];
    const { low: diaslow_man, high: diashigh_man, avg: diasavg_man } = diastolic_manual[i];
    const { high: syshigh_ext, low: syslow_ext, avg: sysavg_ext } = systolic_external[i];
    const { low: diaslow_ext, high: diashigh_ext, avg: diasavg_ext } = diastolic_external[i];

    let syshigh = syshigh_man && syshigh_r ? (+syshigh_man > +syshigh_r ? syshigh_man : syshigh_r) : +syshigh_man || +syshigh_r;
    if(syshigh_ext && +syshigh_ext > +syshigh) syshigh = syshigh_ext
    let syslow = syslow_man && syslow_r ? (+syslow_man <= +syslow_r ? +syslow_man : +syslow_r) : +syslow_man || +syslow_r;
    if( syslow_ext && (!syslow || +syslow_ext < +syslow) ) syslow = syslow_ext
    const sys_avg = !!sysavg_man ^ !!sysavg || sysavg_man || sysavg || sysavg_ext ? Math.round((+sysavg_man + +sysavg + +sysavg_ext) / (+!!sysavg_man + +!!sysavg + +!!sysavg_ext)) : null;
    const sys_color = getBarColor('SYSTOLIC', +syshigh_ext, +syshigh_man, +syshigh_r)
    
    let diashigh = diashigh_man && diashigh_r ? +diashigh_man > +diashigh_r ? +diashigh_man : +diashigh_r : +diashigh_man || +diashigh_r;
    if(diashigh_ext && +diashigh_ext > +diashigh) diashigh = diashigh_ext
    let diaslow = diaslow_man && diaslow_r ? (+diaslow_man <= +diaslow_r ? +diaslow_man : +diaslow_r) : +diaslow_man || +diaslow_r;
    if( diaslow_ext && (!diaslow || +diaslow_ext < +diaslow) ) diaslow = diaslow_ext
    const dias_avg = !!diasavg_man ^ !!diasavg || diasavg_man || diasavg || diasavg_ext ? Math.round((+diasavg_man + +diasavg + +diasavg_ext) / (+!!diasavg_man + +!!diasavg + +!!diasavg_ext)) : null;
    const dias_color = getBarColor('DIASTOLIC', +diashigh_ext, +diashigh_man, diashigh_r);
    const empty = !sys_avg && !dias_avg;
    
    return { 
      date, syshigh, syslow, sysopen: syslow, sysclose: syshigh, diashigh, diaslow,
      diasopen: diaslow, diasclose: diashigh, sys_color, dias_color, sys_avg, dias_avg, empty,
    };
  });
  const fixLabelXPosition = width/systolic.length/3.7

  const x = d3
    .scaleBand()
    .domain(d3.utcDay.range(data[0].date, +data[data.length - 1].date + 1))
    .range([margin.left, width - margin.right])
    .padding(0.4);

  const y = d3
    .scaleLinear()
    .domain([MIN_BP_DIA, MAX_BP_SYS])
    .nice()
    .range([height - margin.bottom, margin.top]);

  const xAxis = (g) =>
    g.attr('transform', `translate(0,${height - margin.bottom - 2})`)
      .call(d3.axisBottom(x)
        .tickValues(d3.utcDay.every(1).range(data[0].date, +data[data.length - 1].date + 1))
        .tickFormat((d) => moment(d).format('ddd DD'))
        .tickSize(0)
      )
      .call((g) =>
        g.selectAll('.tick line')
          .clone()
          .attr('stroke-opacity', 0.2)
          .attr('transform', `translate(0,${-height + margin.top + margin.bottom + 3})`)
          .attr('y2', height - margin.top - margin.bottom)
      )
      .call((g) => g.select('.domain').remove());

  const yAxis = (g) =>
    g.attr('transform', `translate(${margin.left - 10},0)`)
      .call(d3
        .axisLeft(y)
        .tickValues(d3.scaleLinear().domain(y.domain()).ticks())
        .tickSize(0)
        .tickPadding(10)
      ).call((g) =>
        g.selectAll('.tick line')
          .clone()
          .attr('stroke-opacity', 0.2)
          .attr('x2', width - margin.left - margin.right)
      )
      .call((g) => g.select('.domain').remove());

  const systolicLineGenerate = d3
    .line()
    .defined((d) => {
      return d.sysopen && d.sysclose;
    })
    .x((d) => x(d.date) + x.bandwidth() / 2)
    .y((d) => y(d.sys_avg));

  const diastolicLineGenerate = d3
    .line()
    .defined((d) => {
      return d.diasopen && d.diasclose;
    })
    .x((d) => x(d.date) + x.bandwidth() / 2)
    .y((d) => y(d.dias_avg));

  const svg = d3.create('svg').attr('width', width).attr('height', height);

  if(appConfigThresholds === 'on'){
    const diastolicBackgroundColor = svg
    .append('rect')
    .attr('x', x(data[0].date))
    .attr('y', y(blood_pressure_threshold.high_DIA || 80))
    .attr('width', Math.abs(x(data[x.domain().length - 1].date) - x(data[0].date)) + x.bandwidth())
    .attr('height', Math.abs(y(blood_pressure_threshold.high_DIA || 80) - y(blood_pressure_threshold.low_DIA || 60)))
    .attr('fill', COLORS.DIASTOLIC_BACKGROUND_COLOR)
    .attr('opacity', 0.12);

  const systolicBackgroundColor = svg
    .append('rect')
    .attr('x', x(data[0].date))
    .attr('y', y(blood_pressure_threshold.high_SYS || 120))
    .attr('width', Math.abs(x(data[x.domain().length - 1].date) - x(data[0].date)) + x.bandwidth())
    .attr('height', Math.abs(y(blood_pressure_threshold.high_SYS || 120) - y(blood_pressure_threshold.low_SYS || 90)))
    .attr('fill', COLORS.SYSTOLIC_BACKGROUND_COLOR)
    .attr('opacity', 0.09);
  }

  const systolicLine = svg
    .append('path')
    .attr('d', systolicLineGenerate(data))
    .attr('fill', 'none')
    .attr('stroke', 'black');

  const diastolicLine = svg
    .append('path')
    .attr('d', diastolicLineGenerate(data))
    .attr('fill', 'none')
    .attr('stroke', 'black');

  svg.append('g').call(xAxis).selectAll('.tick text').call(wrap, x.bandwidth());
  svg.append('g').call(yAxis);

  const yAxisLabelDate = svg
    .append('text')
    .attr('x', margin.left - 40)
    .attr('y', margin.top - 20)
    .style('font-size', '12px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.units.mmhg`) : 'mmHg');

    const avgLegendRect = svg
    .append('circle')
    .attr('r', 8)
    .attr('cx', margin.left + 15)
    .attr('cy', margin.top - 25)
    .attr('stroke-width', 1)
    .attr('fill', 'white')
    .attr('stroke', '#000')
    .attr('width', 22)
    .attr('height', 22)

  const avgLegendInlineText = svg
    .append('text')
    .attr('x', margin.left + 11)
    .attr('y', margin.top - 21)
    .style('font-size', '11px')
    .attr('font-weight', '100')
    .text('#');

  const avgLegendText = svg
    .append('text')
    .attr('x', margin.left + 30)
    .attr('y', margin.top - 18)
    .style('font-size', '15px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.legends.avg`) : 'Average');

  const missingValuesLegendRect = svg
    .append('rect')
    .attr('x', margin.left + 10)
    .attr('y', margin.top - 60)
    .attr('width', 10)
    .attr('height', 10)
    .attr('opacity', 0.11)
    .attr('fill', COLORS.EMPTY_COLOR);

  const missingValuesLegendText = svg
    .append('text')
    .attr('x', margin.left + 25)
    .attr('y', margin.top - 50)
    .style('font-size', '15px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.legends.no_measure`) : 'No measurements');

  const xAxisLabelDate = svg
    .append('text')
    .attr('x', 20)
    .attr('y', height - margin.bottom + 20)
    .style('font-size', '12px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.general_words.date`) : 'Date');

  const xAxisLabelDay = svg
    .append('text')
    .attr('x', 20)
    .attr('y', height - margin.bottom + 33)
    .style('font-size', '12px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.general_words.day`) : 'Day');

  const g = svg
    .append('g')
    .selectAll('g')
    .data(data)
    .join('g')
    .attr('transform', (d) => `translate(${x(d.date)},0)`);


  const emptyGroup = g.append('g').filter(d => d.empty);
  const emptyBar = emptyGroup
    .append('line')
    .attr('y1', (d) => y(MIN_BP_DIA))
    .attr('y2', (d) => y(MAX_BP_SYS))
    .attr('stroke-width', 35)
    .attr('stroke', (d) => COLORS.EMPTY_COLOR)
    .attr('opacity', 0.07)
    .attr('transform', `translate(${(x.bandwidth()) / 2},0)`);

  const systolicCandleGroup = g.append('g').filter(d => (d.sysopen && d.sysclose));
  const systolicCandle = systolicCandleGroup
    .append('line')
    .attr('y1', (d) => y(d.sysopen))
    .attr('y2', (d) => y(d.sysclose))
    .attr('stroke-width', BAR_WIDTH)
    .attr('stroke', (d) => d.sys_color)
    .attr('transform', `translate(${(x.bandwidth()) / 2},0)`);

  const diastolicCandleGroup = g.append('g').filter(d => (d.diasopen && d.diasclose));
  const systolicCircleTextGroup = g.append('g').filter(d => (d.diasopen && d.diasclose));
  const diastolicCircleTextGroup = g.append('g').filter(d => (d.diasopen && d.diasclose));
  const diastolicCandle = diastolicCandleGroup
    .append('line')
    .attr('y1', (d) => y(d.diasopen))
    .attr('y2', (d) => y(d.diasclose))
    .attr('stroke-width', BAR_WIDTH)
    .attr('stroke', (d) => d.dias_color)
    .attr('transform', `translate(${(x.bandwidth()) / 2},0)`);

  const diastolicCircle = diastolicCircleTextGroup
    .append('circle')
    .attr('r', 10)
    .attr('cy', (d) => y(d.dias_avg))
    .attr('stroke-width', 1)
    .attr('fill', 'white')
    .attr('stroke', (d) => d.dias_color)
    .attr('transform', `translate(${(x.bandwidth()) / 2},0)`)
    .style("cursor", "pointer")
    .on("click", (e, d) => onClick(d.date));

  // diastolicCircle.raise();



  // systolicCircle.raise();

  const diastolicCircleText = diastolicCircleTextGroup
    .append('text')
    .filter(d => d.dias_avg)
    .attr('text-anchor', 'middle')
    .attr('dy', (d) => y(d.dias_avg) + 4)
    .style('font-size', '10px')
    .attr('font-weight', '400')
    .attr('transform', `translate(${(x.bandwidth()) / 2},0)`)
    .append('tspan')
    .text((d) => d.dias_avg)
    .style("cursor", "pointer")
    .on("click", (e, d) => onClick(d.date));

  // diastolicCircleText.raise();
  const systolicCircle = systolicCircleTextGroup
    .append('circle')
    .attr('r', 10)
    .attr('cy', (d) => y(d.sys_avg))
    .attr('stroke-width', 1)
    .attr('fill', 'white')
    .attr('stroke', (d) => d.sys_color)
    .attr('transform', `translate(${(x.bandwidth()) / 2},0)`)
    .style("cursor", "pointer")
    .on("click", (e, d) => onClick(d.date));

  const systolicCircleText = systolicCircleTextGroup
    .append('text')
    .attr('text-anchor', 'middle')
    .attr('dy', (d) => y(d.sys_avg) + 4
    )
    .style('font-size', '10px')
    .attr('font-weight', '400')
    .attr('transform', `translate(${(x.bandwidth()) / 2},0)`)
    .text((d) => d.sys_avg)
    .style("cursor", "pointer")
    .on("click", (e, d) => onClick(d.date));

  // systolicCircleText.raise();

  const gLegend = svg.append('g').attr('transform', (d) => `translate(${width-800},${margin.top - 60})`);

  if(1==1){
    const systolicRangeLegendRect = gLegend
    .append('rect')
    .attr('x', -175)
    .attr('y', 0)
    .attr('width', 10)
    .attr('height', 10)
    .attr('fill', COLORS.SYSTOLIC_MIX_COLOR)

  const systolicRangeLegendText = gLegend
    .append('text')
    .attr('x', -160)
    .attr('y', 10)
    .style('font-size', '15px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_TAG}.sys_mix`) : 'Systolic mix measurements');

    const diastolicRangeLegendRect = gLegend
    .append('rect')
    .attr('x', -175)
    .attr('y', 30)
    .attr('width', 10)
    .attr('height', 10)
    .attr('fill', COLORS.DIASTOLIC_MIX_COLOR)

  const diastolicRangeLegendText = gLegend
    .append('text')
    .attr('x', -160)
    .attr('y', 40)
    .style('font-size', '15px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_TAG}.dia_mix`) : 'Diastolic mix measurements')
    .style("cursor", "pointer")
    .on("click", onClick);
  }

  const systolicManualLegendRect = gLegend
    .append('rect')
    .attr('x', 45)
    .attr('y', 0)
    .attr('width', 10)
    .attr('height', 10)
    .attr('fill', COLORS.SYSTOLIC_MANUAL_COLOR);

  const systolicManualLegendText = gLegend
    .append('text')
    .attr('x', 60)
    .attr('y', 10)
    .style('font-size', '15px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_TAG}.sys_manual`) : 'Systolic manual measurements');

  const diastolicManualLegendRect = gLegend
    .append('rect')
    .attr('x', 45)
    .attr('y', 30)
    .attr('width', 10)
    .attr('height', 10)
    .attr('fill', COLORS.DIASTOLIC_MANUAL_COLOR);

  const diastolicManualLegendText = gLegend
    .append('text')
    .attr('x', 60)
    .attr('y', 40)
    .style('font-size', '15px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_TAG}.dia_manual`) : 'Diastolic manual measurements');

  const systolicLegendRect = gLegend
    .append('rect')
    .attr('x', 293)
    .attr('y', 0)
    .attr('width', 10)
    .attr('height', 10)
    .attr('fill', COLORS.SYSTOLIC_COLOR);

  const systolicLegendText = gLegend
    .append('text')
    .attr('x', 308)
    .attr('y', 10)
    .style('font-size', '15px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_TAG}.sys_device`) : 'Systolic device measurements');

  const diastolicLegendRect = gLegend
    .append('rect')
    .attr('x', 293)
    .attr('y', 30)
    .attr('width', 10)
    .attr('height', 10)
    .attr('fill', COLORS.DIASTOLIC_COLOR);

  const diastolicLegendText = gLegend
    .append('text')
    .attr('x', 308)
    .attr('y', 40)
    .style('font-size', '15px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_TAG}.dia_device`) : 'Diastolic device measurements');

  const systolicExternalLegendRect = gLegend
    .append('rect')
    .attr('x', 535)
    .attr('y', 0)
    .attr('width', 10)
    .attr('height', 10)
    .attr('fill', COLORS.SYSTOLIC_EXTERNAL_COLOR);

  const systolicExternalLegendText = gLegend
    .append('text')
    .attr('x', 550)
    .attr('y', 10)
    .style('font-size', '15px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_TAG}.sys_external`) : 'Systolic external measurements');

  const diastolicExternalLegendRect = gLegend
    .append('rect')
    .attr('x', 535)
    .attr('y', 30)
    .attr('width', 10)
    .attr('height', 10)
    .attr('fill', COLORS.DIASTOLIC_EXTERNAL_COLOR);

  const diastolicExternalLegendText = gLegend
    .append('text')
    .attr('x', 550)
    .attr('y', 40)
    .style('font-size', '15px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_TAG}.dia_external`) : 'Diastolic external measurements');

/*   const respiratoryLegendAverageCircle = gLegend
    .append('circle')
    .attr('cx', -216)
    .attr('cy', 20)
    .attr('r', 7)
    .attr('stroke-width', 1)
    .attr('fill', 'white')
    .attr('stroke', '#4AA5D4');

  const respiratoryLegendAverageText = gLegend
    .append('text')
    .attr('x', -220)
    .attr('y', 25)
    .style('font-size', '12px')
    .attr('font-weight', '100')
    .text('#\u00A0\u00A0Average Respiratory rate');
 */
  return svg.node();
};

function getBarColor (type, externalVal, manualVal, deviceVal){
  const isMixVal = [externalVal, manualVal, deviceVal].filter(val => val).length > 1
  if(isMixVal) return COLORS[`${type}_MIX_COLOR`]
  if(externalVal) return COLORS[`${type}_EXTERNAL_COLOR`]
  if(manualVal) return COLORS[`${type}_MANUAL_COLOR`]
  if(deviceVal) return COLORS[`${type}_COLOR`]
}
